// frontend/src/components/ComplaintView.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Button,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Stack,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import MessageIcon from '@mui/icons-material/Message';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import PhoneIcon from '@mui/icons-material/Phone';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CallIcon from '@mui/icons-material/Call';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import HistoryIcon from '@mui/icons-material/History';
import ReplayIcon from '@mui/icons-material/Replay';
import '../../App.css'; // Global CSS with CSS variables

// Import your custom api instance
import api from '../../services/api';
import SmsStatus from '../SmsStatus';
import PriorityChip from '../common/PriorityChip';

// Get base URL for protected file endpoints
const baseURL = process.env.NODE_ENV === 'production' 
  ? 'https://api.kcvenugopal.online/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

// Helper to fix Windows-style paths and properly encode for URLs
const fixPathForUrl = (path) => {
  if (!path) return '';
  // Normalize path separators
  let normalized = path.replace(/\\/g, '/').replace(/^\.\.\//, '');
  // Split into segments and encode each part (to preserve path structure)
  // This handles spaces and special characters in filenames
  const segments = normalized.split('/');
  const encodedSegments = segments.map(segment => encodeURIComponent(segment));
  return encodedSegments.join('/');
};

// Returns a background color based on the status
const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case 'new':
      return '#2196f3'; // Blue
    case 'pending':
      return '#ff9800'; // Orange
    case 'resolved':
      return '#4caf50'; // Green
    default:
      return '#757575'; // Grey
  }
};

const ComplaintView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [complaint, setComplaint] = useState(null);
  const [complaintPdfUrl, setComplaintPdfUrl] = useState(null);
  const [idProofPdfUrl, setIdProofPdfUrl] = useState(null);
  const [assemblies, setAssemblies] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [pdfLoadError, setPdfLoadError] = useState({ complaint: false, idProof: false });
  const [pdfLoading, setPdfLoading] = useState({ complaint: true, idProof: true });
  const [copiedField, setCopiedField] = useState(null);

  // Workflow: assignment, notes and reopen
  const isAdmin = localStorage.getItem('role') === 'admin';
  const [users, setUsers] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenSendSms, setReopenSendSms] = useState(false);
  const [reopenSaving, setReopenSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleCopyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    api
      .get(`/complaints/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((response) => {
        const data = response.data;
        setComplaint(data);

        // Helper function to validate file path
        const isValidFilePath = (path) => {
          return path && 
                 path !== null && 
                 path !== 'null' && 
                 path !== undefined && 
                 path !== 'undefined' &&
                 typeof path === 'string' &&
                 path.trim() !== '';
        };

        if (isValidFilePath(data.complaintPdfFilePath)) {
          // Use protected endpoint: /api/files/:complaintId/complaint-pdf
          // Include token in query parameter for iframe compatibility
          const token = localStorage.getItem('token');
          const url = `${baseURL}/files/${data.complaintId}/complaint-pdf${token ? `?token=${encodeURIComponent(token)}` : ''}`;
          setComplaintPdfUrl(url);
          console.log('📄 Complaint PDF URL (protected):', url);
          console.log('📄 Complaint ID:', data.complaintId);
          console.log('📄 Base URL:', baseURL);
          console.log('📄 File path from data:', data.complaintPdfFilePath);
          // Verify URL is correct format
          if (url.includes('/uploads/')) {
            console.error('❌ ERROR: URL contains /uploads/ - should use /api/files/ instead!');
          }
          
          // Set timeout to detect if PDF doesn't load within 10 seconds
          setTimeout(() => {
            setPdfLoading(prev => {
              if (prev.complaint) {
                console.warn('⚠️ Complaint PDF loading timeout - checking if file exists');
                // Test if the URL is accessible
                fetch(url, { method: 'HEAD' })
                  .then(response => {
                    if (!response.ok) {
                      console.error('❌ Complaint PDF not accessible:', response.status);
                      setPdfLoadError(prev => ({ ...prev, complaint: true }));
                    } else {
                      console.log('✅ Complaint PDF is accessible, but may not be rendering in iframe');
                    }
                  })
                  .catch(err => {
                    console.error('❌ Error checking complaint PDF:', err);
                    setPdfLoadError(prev => ({ ...prev, complaint: true }));
                  });
                return { ...prev, complaint: false };
              }
              return prev;
            });
          }, 10000);
        } else {
          // Explicitly set to null if path is invalid
          setComplaintPdfUrl(null);
          setPdfLoading(prev => ({ ...prev, complaint: false }));
          console.log('📄 Complaint PDF path is null or invalid, skipping URL construction');
        }
        
        if (isValidFilePath(data.idProofFilePath)) {
          // Use protected endpoint: /api/files/:complaintId/id-proof
          // Include token in query parameter for iframe compatibility
          const token = localStorage.getItem('token');
          const url = `${baseURL}/files/${data.complaintId}/id-proof${token ? `?token=${encodeURIComponent(token)}` : ''}`;
          setIdProofPdfUrl(url);
          console.log('🆔 ID Proof PDF URL (protected):', url);
          console.log('🆔 Complaint ID:', data.complaintId);
          console.log('🆔 Base URL:', baseURL);
          console.log('🆔 File path from data:', data.idProofFilePath);
          // Verify URL is correct format
          if (url.includes('/uploads/')) {
            console.error('❌ ERROR: URL contains /uploads/ - should use /api/files/ instead!');
          }
          
          // Set timeout to detect if PDF doesn't load within 10 seconds
          setTimeout(() => {
            setPdfLoading(prev => {
              if (prev.idProof) {
                console.warn('⚠️ ID Proof PDF loading timeout - checking if file exists');
                // Test if the URL is accessible
                fetch(url, { method: 'HEAD' })
                  .then(response => {
                    if (!response.ok) {
                      console.error('❌ ID Proof PDF not accessible:', response.status);
                      setPdfLoadError(prev => ({ ...prev, idProof: true }));
                    } else {
                      console.log('✅ ID Proof PDF is accessible, but may not be rendering in iframe');
                    }
                  })
                  .catch(err => {
                    console.error('❌ Error checking ID Proof PDF:', err);
                    setPdfLoadError(prev => ({ ...prev, idProof: true }));
                  });
                return { ...prev, idProof: false };
              }
              return prev;
            });
          }, 10000);
        } else {
          // Explicitly set to null if path is invalid
          setIdProofPdfUrl(null);
          setPdfLoading(prev => ({ ...prev, idProof: false }));
          console.log('🆔 ID Proof PDF path is null or invalid, skipping URL construction');
        }
      })
      .catch((error) => console.error('Error fetching complaint details:', error));
  }, [id]);

  // Fetch assemblies to convert legislativeAssembly id to its display name
  useEffect(() => {
    api
      .get('/assemblies')
      .then((res) => setAssemblies(res.data))
      .catch((err) => console.error('Error fetching assemblies:', err));
  }, []);

  // Fetch users list (admin only) for the assign dialog and to resolve bare assignee ids
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get('/users')
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('Error fetching users:', err));
  }, [isAdmin]);

  if (!complaint) {
    return (
      <Container maxWidth="md" sx={{ mt: '5rem' }}>
        <Typography variant="h6" align="center">
          Loading complaint detaidsdsls...
        </Typography>
      </Container>
    );
  }

  // Convert assembly id to name
  const assemblyObj = assemblies.find(
    (a) => a._id.toString() === complaint.legislativeAssembly?.toString()
  );
  const assemblyName = assemblyObj ? assemblyObj.name : complaint.legislativeAssembly;

  // ---- Workflow helpers (assignment / notes / reopen) ----
  const formatDateTime = (value) =>
    value
      ? new Date(value).toLocaleString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      : 'N/A';

  // assignedTo may be a populated object {_id, username}, a bare id string, or null
  const getAssigneeName = (assignedTo) => {
    if (!assignedTo) return null;
    if (typeof assignedTo === 'object') return assignedTo.username || assignedTo._id || 'Unknown';
    const match = users.find((u) => u._id === assignedTo);
    return match ? match.username : assignedTo;
  };

  const handleOpenAssignDialog = () => {
    const current = complaint.assignedTo;
    setAssignTo(current ? (typeof current === 'object' ? current._id : current) : '');
    setAssignNote('');
    setAssignDialogOpen(true);
  };

  const handleAssignSave = async () => {
    setAssignSaving(true);
    try {
      const res = await api.put(`/complaints/${id}/assign`, {
        assignedTo: assignTo || null,
        note: assignNote
      });
      const updated =
        res.data && res.data.complaint && res.data.complaint._id
          ? res.data.complaint
          : res.data && res.data._id
          ? res.data
          : null;
      if (updated) {
        setComplaint((prev) => ({ ...prev, ...updated }));
      } else {
        // Fallback: merge locally when the API doesn't echo the updated complaint
        const selectedUser = users.find((u) => u._id === assignTo) || null;
        const now = new Date().toISOString();
        setComplaint((prev) => ({
          ...prev,
          assignedTo: selectedUser,
          assignedAt: selectedUser ? now : null,
          assignmentHistory: [
            ...(prev.assignmentHistory || []),
            { assignedTo: selectedUser, assignedBy: null, at: now, note: assignNote }
          ]
        }));
      }
      setAssignDialogOpen(false);
      setSnackbar({
        open: true,
        message: assignTo ? 'Complaint assignment updated' : 'Complaint unassigned',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating assignment:', err);
      setSnackbar({ open: true, message: 'Failed to update assignment', severity: 'error' });
    } finally {
      setAssignSaving(false);
    }
  };

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setNoteSaving(true);
    try {
      const res = await api.post(`/complaints/${id}/notes`, { text });
      setComplaint((prev) => ({ ...prev, notes: res.data && res.data.notes ? res.data.notes : prev.notes }));
      setNoteText('');
      setSnackbar({ open: true, message: 'Note added', severity: 'success' });
    } catch (err) {
      console.error('Error adding note:', err);
      setSnackbar({ open: true, message: 'Failed to add note', severity: 'error' });
    } finally {
      setNoteSaving(false);
    }
  };

  const handleReopenConfirm = async () => {
    setReopenSaving(true);
    try {
      const res = await api.post(`/complaints/${id}/reopen`, {
        reason: reopenReason,
        sendSms: reopenSendSms
      });
      const updated =
        res.data && res.data.complaint && res.data.complaint._id ? res.data.complaint : null;
      if (updated) {
        setComplaint((prev) => ({ ...prev, ...updated }));
      } else {
        setComplaint((prev) => ({
          ...prev,
          status: 'Open',
          reopenCount: (prev.reopenCount || 0) + 1,
          statusHistory: [
            ...(prev.statusHistory || []),
            { from: prev.status, to: 'Open', at: new Date().toISOString() }
          ]
        }));
      }
      setReopenDialogOpen(false);
      setReopenReason('');
      setReopenSendSms(false);
      setSnackbar({ open: true, message: 'Complaint reopened', severity: 'success' });
    } catch (err) {
      console.error('Error reopening complaint:', err);
      setSnackbar({ open: true, message: 'Failed to reopen complaint', severity: 'error' });
    } finally {
      setReopenSaving(false);
    }
  };

  const canReopen =
    isAdmin && ['resolved', 'solved'].includes((complaint.status || '').toLowerCase());
  const assigneeName = getAssigneeName(complaint.assignedTo);
  const sortedNotes = [...(complaint.notes || [])].sort(
    (a, b) => new Date(b.at) - new Date(a.at)
  );

  // Print complaint details with all available information
  const handlePrintDetails = () => {
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`<html><head><title>Print Complaint Details</title>`);
    printWindow.document.write(`<style>
      body { font-family: Arial, sans-serif; padding: 20px; }
      h1, h2, h3 { color: #333; }
      .section { margin-bottom: 20px; }
      .label { font-weight: bold; }
      .row { margin-bottom: 10px; }
    </style>`);
    printWindow.document.write(`</head><body>`);
    printWindow.document.write(`<h1>Complaint Details</h1>`);
    printWindow.document.write(
      `<div class="section row"><span class="label">Complaint ID:</span> ${complaint.complaintId}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Name:</span> ${complaint.name}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Parent Name:</span> ${complaint.parentName}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Assembly:</span> ${assemblyName}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Panchayath:</span> ${complaint.panchayath || 'N/A'}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Ward No:</span> ${complaint.wardNo || 'N/A'}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Address:</span> ${complaint.address}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Mobile Number:</span> ${complaint.mobileNumber}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Alternate Mobile Number:</span> ${complaint.alternateMobileNumber || 'N/A'}</div>`
    );
    if (complaint.referredMobileNumber) {
      printWindow.document.write(
        `<div class="section row"><span class="label">Referred Mobile:</span> ${complaint.referredMobileNumber}</div>`
      );
    }
    printWindow.document.write(
      `<div class="section row"><span class="label">Category:</span> ${complaint.category}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Subject:</span> ${complaint.subject}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">ID Proof Type:</span> ${complaint.idProofType}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">ID Number:</span> ${complaint.idNumber}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Reference:</span> ${complaint.reference}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Complaint Date:</span> ${new Date(
        complaint.complaintDate
      ).toLocaleString()}</div>`
    );
    printWindow.document.write(
      `<div class="section row"><span class="label">Status:</span> ${complaint.status}</div>`
    );
    printWindow.document.write(
      `<div class="section"><span class="label">Complaint Details:</span><br/> ${complaint.complaintDetails}</div>`
    );
    printWindow.document.write(
      `<div class="section"><span class="label">Remarks:</span> ${complaint.remarks || 'N/A'}</div>`
    );
    printWindow.document.write(`</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Print PDF using browser's default print option
  const handlePrintPdf = (pdfUrl) => {
    if (pdfUrl) {
      const win = window.open(pdfUrl, '_blank');
      if (win) {
        win.focus();
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ mt: { xs: 9, sm: 7 }, mb: 4, px: { xs: 1, sm: 2 }, mx: 'auto' }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 3 },
            backgroundColor: 'var(--bg-color)',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderRadius: '4px'
          }}
        >
          {/* Improved Header Section */}
          <Box 
            sx={{ 
              mb: 3,
              pb: 2,
              borderBottom: '2px solid',
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
              borderRadius: 2,
              p: { xs: 2, sm: 2.5 }
            }}
          >
            {/* Top Row: Back Button, Title, Actions */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2,
              flexWrap: 'wrap',
              gap: 1.5
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Tooltip title="Go Back" aria-label="Back">
                  <IconButton 
                    color="primary" 
                    onClick={() => navigate(-1)} 
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      }
                    }}
                  >
                    <ArrowBackIcon fontSize={isMobile ? "small" : "medium"} />
                  </IconButton>
                </Tooltip>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  sx={{ 
                    fontWeight: 700, 
                    color: 'var(--text-color)', 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  Complaint Details
                </Typography>
              </Box>
              
              <Stack 
                direction="row" 
                spacing={1} 
                sx={{ 
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }}
              >
                  <Tooltip title="Edit Complaint" aria-label="Edit Complaint">
                    <IconButton
                      color="secondary"
                      onClick={() => navigate(`/complaints/edit/${complaint._id}`)}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                      }
                    }}
                  >
                    <EditIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Tooltip>
                  <Chip
                    label={complaint.status}
                  size={isMobile ? "small" : "medium"}
                    sx={{
                      backgroundColor: getStatusColor(complaint.status),
                      color: 'white',
                    fontWeight: 600,
                    boxShadow: `0 2px 4px ${alpha(getStatusColor(complaint.status), 0.3)}`,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                    }}
                  />
                  <PriorityChip
                    priority={complaint.priority}
                    size={isMobile ? "small" : "medium"}
                  />
                  {complaint.reopenCount > 0 && (
                    <Chip
                      label={`Reopened ×${complaint.reopenCount}`}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  {canReopen && (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      startIcon={<ReplayIcon />}
                      onClick={() => setReopenDialogOpen(true)}
                    >
                      Reopen
                    </Button>
                  )}
                  <Tooltip title="Print Complaint Details" aria-label="Print Complaint Details">
                  <IconButton 
                    color="primary" 
                    onClick={handlePrintDetails} 
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      }
                    }}
                  >
                    <PrintIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Tooltip>
              </Stack>
                </Box>

            {/* Bottom Row: Date and Complaint ID */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1,
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    color: 'text.secondary'
                  }}
                >
                  <strong>Date:</strong> {new Date(complaint.complaintDate).toLocaleString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    color: 'text.secondary'
                  }}
                >
                  <strong>ID:</strong> {complaint.complaintId}
              </Typography>
                <Tooltip title={copiedField === 'id' ? 'Copied!' : 'Copy ID'}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopyToClipboard(complaint.complaintId, 'id')}
                    sx={{ p: 0.5 }}
                  >
                    {copiedField === 'id' ? 
                      <CheckCircleIcon fontSize="small" color="success" /> : 
                      <ContentCopyIcon fontSize="small" />
                    }
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          {/* Assignment Section */}
          <Card
            elevation={0}
            sx={{
              mb: { xs: 2, sm: 3 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                p: { xs: 2, sm: 2.5 },
                '&:last-child': { pb: { xs: 2, sm: 2.5 } }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <AssignmentIndIcon color="primary" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Assignment
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                    {assigneeName ? (
                      <>
                        Assigned to <strong>{assigneeName}</strong>
                        {complaint.assignedAt && ` on ${formatDateTime(complaint.assignedAt)}`}
                      </>
                    ) : (
                      'Not assigned'
                    )}
                  </Typography>
                </Box>
              </Box>
              {isAdmin && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AssignmentIndIcon />}
                  onClick={handleOpenAssignDialog}
                >
                  Assign / Reassign
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Tabs Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label="complaint details tabs"
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              sx={{
                '& .MuiTab-root': {
                  minWidth: { xs: 80, sm: 120 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 12px', sm: '12px 16px' }
                }
              }}
            >
              <Tab label="Details" />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                    <MessageIcon fontSize={isMobile ? "small" : "medium"} />
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    SMS Status
                    </Box>
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                    <HistoryIcon fontSize={isMobile ? "small" : "medium"} />
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Notes & History
                    </Box>
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {activeTab === 0 && (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* Left Column: Details (8/12) */}
              <Grid item xs={12} md={8} order={{ xs: 1, md: 1 }}>
                <Box sx={{ px: { xs: 0, sm: 2 } }}>
                  {(isMobile || isTablet) ? (
                    // Mobile/Tablet: Accordion/Card View
                    <Stack spacing={2}>
                      {/* Personal Information Card */}
                      <Card elevation={2} sx={{ borderRadius: 2 }}>
                        <Accordion defaultExpanded sx={{ boxShadow: 'none' }}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonIcon color="primary" />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Personal Information
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Complaint ID:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {complaint.complaintId}
                                  </Typography>
                                  <Tooltip title={copiedField === 'id' ? 'Copied!' : 'Copy ID'}>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleCopyToClipboard(complaint.complaintId, 'id')}
                                    >
                                      {copiedField === 'id' ? 
                                        <CheckCircleIcon fontSize="small" color="success" /> : 
                                        <ContentCopyIcon fontSize="small" />
                                      }
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                              <Divider />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  Name:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {complaint.name}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  Parent Name:
                                </Typography>
                                <Typography variant="body1">
                                  {complaint.parentName}
                                </Typography>
                              </Box>
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      </Card>

                      {/* Location & Contact Card */}
                      <Card elevation={2} sx={{ borderRadius: 2 }}>
                        <Accordion defaultExpanded sx={{ boxShadow: 'none' }}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationOnIcon color="primary" />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Location & Contact
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1.5}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  Assembly:
                                </Typography>
                                <Typography variant="body1">{assemblyName}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  Panchayath:
                                </Typography>
                                <Typography variant="body1">{complaint.panchayath || 'N/A'}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  Ward No:
                                </Typography>
                                <Typography variant="body1">{complaint.wardNo || 'N/A'}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  Address:
                                </Typography>
                                <Typography variant="body1">{complaint.address}</Typography>
                              </Box>
                              <Divider />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                                  Mobile:
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                  <Typography variant="body1" sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>{complaint.mobileNumber}</Typography>
                                  <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Call">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => window.open(`tel:${complaint.mobileNumber}`, '_self')}
                                        sx={{
                                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                          '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                          },
                                          border: '1px solid',
                                          borderColor: alpha(theme.palette.primary.main, 0.3)
                                        }}
                                      >
                                        <CallIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="WhatsApp">
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => window.open(`https://wa.me/${complaint.mobileNumber.replace(/[^0-9]/g, '')}`, '_blank')}
                                        sx={{
                                          backgroundColor: alpha(theme.palette.success.main, 0.1),
                                          '&:hover': {
                                            backgroundColor: alpha(theme.palette.success.main, 0.2),
                                          },
                                          border: '1px solid',
                                          borderColor: alpha(theme.palette.success.main, 0.3)
                                        }}
                                      >
                                        <WhatsAppIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                              </Box>
                              {complaint.alternateMobileNumber && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                                    Alt. Mobile:
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                    <Typography variant="body1" sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>{complaint.alternateMobileNumber}</Typography>
                                    <Stack direction="row" spacing={0.5}>
                                      <Tooltip title="Call">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => window.open(`tel:${complaint.alternateMobileNumber}`, '_self')}
                                          sx={{
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            '&:hover': {
                                              backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                            },
                                            border: '1px solid',
                                            borderColor: alpha(theme.palette.primary.main, 0.3)
                                          }}
                                        >
                                          <CallIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="WhatsApp">
                                        <IconButton
                                          size="small"
                                          color="success"
                                          onClick={() => window.open(`https://wa.me/${complaint.alternateMobileNumber.replace(/[^0-9]/g, '')}`, '_blank')}
                                          sx={{
                                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                                            '&:hover': {
                                              backgroundColor: alpha(theme.palette.success.main, 0.2),
                                            },
                                            border: '1px solid',
                                            borderColor: alpha(theme.palette.success.main, 0.3)
                                          }}
                                        >
                                          <WhatsAppIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </Box>
                                </Box>
                              )}
                              {complaint.referredMobileNumber && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                                    Referred Mobile:
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                    <Typography variant="body1" sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>{complaint.referredMobileNumber}</Typography>
                                    <Stack direction="row" spacing={0.5}>
                                      <Tooltip title="Call">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => window.open(`tel:${complaint.referredMobileNumber}`, '_self')}
                                          sx={{
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            '&:hover': {
                                              backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                            },
                                            border: '1px solid',
                                            borderColor: alpha(theme.palette.primary.main, 0.3)
                                          }}
                                        >
                                          <CallIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="WhatsApp">
                                        <IconButton
                                          size="small"
                                          color="success"
                                          onClick={() => window.open(`https://wa.me/${complaint.referredMobileNumber.replace(/[^0-9]/g, '')}`, '_blank')}
                                          sx={{
                                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                                            '&:hover': {
                                              backgroundColor: alpha(theme.palette.success.main, 0.2),
                                            },
                                            border: '1px solid',
                                            borderColor: alpha(theme.palette.success.main, 0.3)
                                          }}
                                        >
                                          <WhatsAppIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </Box>
                                </Box>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      </Card>

                      {/* Complaint & ID Details Card */}
                      <Card elevation={2} sx={{ borderRadius: 2 }}>
                        <Accordion defaultExpanded sx={{ boxShadow: 'none' }}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DescriptionIcon color="primary" />
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Complaint & ID Details
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1.5}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  Category:
                                </Typography>
                                <Chip label={complaint.category} size="small" color="primary" variant="outlined" />
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  Subject:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {complaint.subject}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  ID Proof Type:
                                </Typography>
                                <Typography variant="body1">{complaint.idProofType}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  ID Number:
                                </Typography>
                                <Typography variant="body1">{complaint.idNumber}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  Reference:
                                </Typography>
                                <Typography variant="body1">{complaint.reference}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                  Status:
                                </Typography>
                                <Chip 
                                  label={complaint.status} 
                                  size="small"
                                  sx={{ 
                                    backgroundColor: getStatusColor(complaint.status), 
                                    color: 'white', 
                                    fontWeight: 600 
                                  }}
                                />
                              </Box>
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      </Card>

                      {/* Complaint Details Card */}
                      <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <DescriptionIcon color="primary" />
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              Complaint Details
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              p: 2.5,
                              backgroundColor: alpha(theme.palette.primary.main, 0.03),
                              borderLeft: '4px solid',
                              borderColor: 'primary.main',
                              borderRadius: 1,
                              mb: 2,
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              maxHeight: { xs: '400px', sm: '500px' },
                              overflowY: 'auto',
                              '&::-webkit-scrollbar': {
                                width: '8px',
                              },
                              '&::-webkit-scrollbar-track': {
                                backgroundColor: alpha(theme.palette.grey[300], 0.1),
                                borderRadius: '4px',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.3),
                                borderRadius: '4px',
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.5),
                                },
                              },
                              '& p': {
                                marginBottom: '12px',
                                lineHeight: 1.7,
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              },
                              '& ul, & ol': {
                                paddingLeft: '20px',
                                marginBottom: '12px',
                              },
                              '& li': {
                                marginBottom: '8px',
                                lineHeight: 1.7,
                              }
                            }}
                          >
                            <div
                              style={{ 
                                wordBreak: 'break-word', 
                                overflowWrap: 'break-word',
                                lineHeight: 1.7
                              }}
                              dangerouslySetInnerHTML={{ __html: complaint.complaintDetails }}
                            />
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                              Remarks:
                            </Typography>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                p: 1.5,
                                backgroundColor: alpha(theme.palette.grey[500], 0.05),
                                borderRadius: 1,
                                fontStyle: complaint.remarks ? 'normal' : 'italic',
                                color: complaint.remarks ? 'text.primary' : 'text.secondary'
                              }}
                            >
                              {complaint.remarks || 'No remarks available'}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Stack>
                  ) : (
                    // Desktop: Original Grid Layout
                    <>
                  <Grid container spacing={2}>
                    {/* Division 1: Personal Information */}
                    <Grid item xs={12}>
                        <Typography 
                          variant="h6" 
                          gutterBottom 
                          sx={{ 
                            color: 'var(--text-color)',
                            fontWeight: 600
                          }}
                        >
                        Personal Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        <strong>ID:</strong> {complaint.complaintId}
                      </Typography>
                          <Tooltip title={copiedField === 'id' ? 'Copied!' : 'Copy ID'}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleCopyToClipboard(complaint.complaintId, 'id')}
                            >
                              {copiedField === 'id' ? 
                                <CheckCircleIcon fontSize="small" color="success" /> : 
                                <ContentCopyIcon fontSize="small" />
                              }
                            </IconButton>
                          </Tooltip>
                        </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>Name:</strong> {complaint.name}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>Parent Name:</strong> {complaint.parentName}
                      </Typography>
                    </Grid>

                    {/* Division 2: Location & Contact */}
                    <Grid item xs={12}>
                        <Typography 
                          variant="h6" 
                          gutterBottom 
                          sx={{ 
                            color: 'var(--text-color)',
                            fontWeight: 600
                          }}
                        >
                        Location & Contact Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>Assembly:</strong> {assemblyName}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>Panchayath:</strong> {complaint.panchayath || 'N/A'}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>Ward No:</strong> {complaint.wardNo || 'N/A'}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>Address:</strong> {complaint.address}
                      </Typography>
                        <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                            sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', mb: 1, fontWeight: 600 }}
                      >
                            <strong>Mobile:</strong>
                      </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
                              {complaint.mobileNumber}
                            </Typography>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Call">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => window.open(`tel:${complaint.mobileNumber}`, '_self')}
                                  sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                    },
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.primary.main, 0.3)
                                  }}
                                >
                                  <CallIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="WhatsApp">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => window.open(`https://wa.me/${complaint.mobileNumber.replace(/[^0-9]/g, '')}`, '_blank')}
                                  sx={{
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.success.main, 0.2),
                                    },
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.success.main, 0.3)
                                  }}
                                >
                                  <WhatsAppIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                        </Box>
                        {complaint.alternateMobileNumber && (
                          <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                              sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', mb: 1, fontWeight: 600 }}
                      >
                              <strong>Alt. Mobile:</strong>
                      </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
                                {complaint.alternateMobileNumber}
                              </Typography>
                              <Stack direction="row" spacing={0.5}>
                                <Tooltip title="Call">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => window.open(`tel:${complaint.alternateMobileNumber}`, '_self')}
                                    sx={{
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                      '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                      },
                                      border: '1px solid',
                                      borderColor: alpha(theme.palette.primary.main, 0.3)
                                    }}
                                  >
                                    <CallIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="WhatsApp">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => window.open(`https://wa.me/${complaint.alternateMobileNumber.replace(/[^0-9]/g, '')}`, '_blank')}
                                    sx={{
                                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                                      '&:hover': {
                                        backgroundColor: alpha(theme.palette.success.main, 0.2),
                                      },
                                      border: '1px solid',
                                      borderColor: alpha(theme.palette.success.main, 0.3)
                                    }}
                                  >
                                    <WhatsAppIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          </Box>
                        )}
                      {complaint.referredMobileNumber && (
                          <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle1"
                              sx={{ wordBreak: 'break-word', overflowWrap: 'break-word', mb: 1, fontWeight: 600 }}
                        >
                              <strong>Referred:</strong>
                        </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
                                {complaint.referredMobileNumber}
                              </Typography>
                              <Stack direction="row" spacing={0.5}>
                                <Tooltip title="Call">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => window.open(`tel:${complaint.referredMobileNumber}`, '_self')}
                                    sx={{
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                      '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                      },
                                      border: '1px solid',
                                      borderColor: alpha(theme.palette.primary.main, 0.3)
                                    }}
                                  >
                                    <CallIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="WhatsApp">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => window.open(`https://wa.me/${complaint.referredMobileNumber.replace(/[^0-9]/g, '')}`, '_blank')}
                                    sx={{
                                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                                      '&:hover': {
                                        backgroundColor: alpha(theme.palette.success.main, 0.2),
                                      },
                                      border: '1px solid',
                                      borderColor: alpha(theme.palette.success.main, 0.3)
                                    }}
                                  >
                                    <WhatsAppIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </Box>
                          </Box>
                      )}
                    </Grid>

                    {/* Division 3: Complaint & ID Details */}
                    <Grid item xs={12}>
                        <Typography 
                          variant="h6" 
                          gutterBottom 
                          sx={{ 
                            color: 'var(--text-color)',
                            fontWeight: 600
                          }}
                        >
                        Complaint & ID Details
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>Category:</strong> {complaint.category}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>ID Proof:</strong> {complaint.idProofType}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>ID Number:</strong> {complaint.idNumber}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>Reference:</strong> {complaint.reference}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>Subject:</strong> {complaint.subject}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        <strong>Status:</strong> {complaint.status}
                      </Typography>
                    </Grid>
                  </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Full-Width Complaint Content - Improved */}
                    <Card 
                      elevation={0} 
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        mb: 2
                      }}
                    >
                      <Box sx={{ 
                        p: 2.5,
                        backgroundColor: alpha(theme.palette.primary.main, 0.03),
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DescriptionIcon color="primary" />
                    <Typography
                            variant="h6"
                            sx={{ 
                              wordBreak: 'break-word', 
                              overflowWrap: 'break-word', 
                              fontWeight: 600 
                            }}
                          >
                            Complaint Details
                    </Typography>
                        </Box>
                      </Box>
                    <Box
                      sx={{
                          p: 3,
                          borderLeft: '4px solid',
                          borderColor: 'primary.main',
                        wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          maxHeight: '600px',
                          overflowY: 'auto',
                          '&::-webkit-scrollbar': {
                            width: '10px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: alpha(theme.palette.grey[300], 0.1),
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.3),
                            borderRadius: '4px',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.5),
                            },
                          },
                          '& p': {
                            marginBottom: '16px',
                            lineHeight: 1.8,
                            fontSize: '1rem',
                            color: 'text.primary'
                          },
                          '& ul, & ol': {
                            paddingLeft: '24px',
                            marginBottom: '16px',
                          },
                          '& li': {
                            marginBottom: '10px',
                            lineHeight: 1.8,
                          },
                          '& strong': {
                            fontWeight: 600,
                            color: 'text.primary'
                          },
                          '& h1, & h2, & h3, & h4, & h5, & h6': {
                            marginTop: '16px',
                            marginBottom: '12px',
                            fontWeight: 600
                          }
                      }}
                    >
                      <div
                          style={{ 
                            wordBreak: 'break-word', 
                            overflowWrap: 'break-word',
                            lineHeight: 1.8
                          }}
                        dangerouslySetInnerHTML={{ __html: complaint.complaintDetails }}
                      />
                    </Box>
                    </Card>

                    {/* Remarks Section */}
                    <Card 
                      elevation={0} 
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden'
                      }}
                    >
                      <Box sx={{ 
                        p: 2,
                        backgroundColor: alpha(theme.palette.grey[500], 0.05),
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}>
                    <Typography
                      variant="subtitle1"
                          sx={{ 
                            wordBreak: 'break-word', 
                            overflowWrap: 'break-word', 
                            fontWeight: 600 
                          }}
                        >
                          Remarks
                    </Typography>
                  </Box>
                      <Box sx={{ p: 2.5 }}>
                        <Typography
                          variant="body1"
                          sx={{ 
                            wordBreak: 'break-word', 
                            overflowWrap: 'break-word',
                            fontStyle: complaint.remarks ? 'normal' : 'italic',
                            color: complaint.remarks ? 'text.primary' : 'text.secondary',
                            lineHeight: 1.7
                          }}
                        >
                          {complaint.remarks || 'No remarks available'}
                        </Typography>
                      </Box>
                    </Card>
                    </>
                  )}
                </Box>
              </Grid>

              {/* Right Column: PDF Previews (4/12) */}
              <Grid item xs={12} md={4} order={{ xs: 2, md: 2 }}>
                <Box sx={{ px: { xs: 0, sm: 2 } }}>
                  {complaintPdfUrl && (
                    <Card 
                      elevation={isMobile || isTablet ? 2 : 0} 
                      sx={{ 
                        mb: 3, 
                        borderRadius: isMobile || isTablet ? 2 : 0,
                        p: isMobile || isTablet ? 2 : 0
                      }}
                    >
                      <Typography
                        variant={isMobile ? "body2" : "subtitle1"}
                        gutterBottom
                        sx={{ color: 'var(--text-color)', fontWeight: 600, mb: 2 }}
                      >
                        <strong>Complaint PDF:</strong>
                      </Typography>
                      <Box
                        sx={{
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          maxWidth: '100%'
                        }}
                      >
                        {pdfLoadError.complaint ? (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                              PDF could not be loaded in preview. 
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Button 
                                variant="outlined" 
                                size="small"
                                onClick={() => window.open(complaintPdfUrl, '_blank')}
                              >
                                Open PDF in New Tab
                              </Button>
                              <Button 
                                variant="text" 
                                size="small"
                                onClick={() => {
                                  setPdfLoadError(prev => ({ ...prev, complaint: false }));
                                  setPdfLoading(prev => ({ ...prev, complaint: true }));
                                }}
                              >
                                Retry Preview
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <>
                            {pdfLoading.complaint && (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body2">Loading PDF...</Typography>
                              </Box>
                            )}
                            {/* Try iframe first */}
                            <iframe
                              src={complaintPdfUrl ? `${complaintPdfUrl}#toolbar=0` : ''}
                              width="100%"
                              height={isMobile ? "250px" : isTablet ? "300px" : "400px"}
                              style={{ border: 'none', display: pdfLoading.complaint ? 'none' : 'block' }}
                              title="Complaint PDF Preview"
                              onError={(e) => {
                                console.error('PDF iframe error:', e);
                                setPdfLoadError(prev => ({ ...prev, complaint: true }));
                                setPdfLoading(prev => ({ ...prev, complaint: false }));
                              }}
                              onLoad={(e) => {
                                console.log('✅ PDF iframe loaded:', complaintPdfUrl);
                                setPdfLoading(prev => ({ ...prev, complaint: false }));
                                // Check if iframe loaded successfully
                                try {
                                  const iframe = e.target;
                                  // If iframe content is accessible, check for errors
                                  if (iframe.contentDocument && iframe.contentDocument.body) {
                                    const bodyText = iframe.contentDocument.body.innerText || '';
                                    if (bodyText.includes('404') || bodyText.includes('Not Found') || bodyText.includes('File not found')) {
                                      console.error('❌ PDF not found in iframe content');
                                      setPdfLoadError(prev => ({ ...prev, complaint: true }));
                                    } else {
                                      console.log('✅ PDF content verified in iframe');
                                    }
                                  }
                                } catch (err) {
                                  // Cross-origin restrictions - can't check content
                                  // This is normal for cross-origin PDFs
                                  console.log('ℹ️ PDF iframe loaded (cross-origin, cannot verify content)');
                                }
                              }}
                            />
                            {/* Fallback object tag for browsers that don't support iframe PDFs */}
                            {!pdfLoading.complaint && (
                              <object
                                data={complaintPdfUrl}
                                type="application/pdf"
                                width="100%"
                                height={isMobile ? "250px" : isTablet ? "300px" : "400px"}
                                style={{ display: 'none' }}
                                onError={() => {
                                  console.log('Object tag also failed, showing error');
                                }}
                              >
                                <Typography variant="body2" align="center" sx={{ p: 2 }}>
                                  PDF preview not available. Please use "Open PDF in New Tab" button.
                                </Typography>
                              </object>
                            )}
                          </>
                        )}
                      </Box>
                      <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        spacing={1} 
                        sx={{ mt: 1, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}
                      >
                        <Button 
                          variant="outlined" 
                          startIcon={<FileDownloadIcon />}
                          onClick={() => window.open(complaintPdfUrl, '_blank')}
                          size={isMobile ? "small" : "medium"}
                          fullWidth={isMobile}
                        >
                          Download
                        </Button>
                        <Button 
                          variant="outlined" 
                          startIcon={<PrintIcon />}
                          onClick={() => handlePrintPdf(complaintPdfUrl)}
                          size={isMobile ? "small" : "medium"}
                          fullWidth={isMobile}
                        >
                          Print
                        </Button>
                      </Stack>
                    </Card>
                  )}
                  {idProofPdfUrl && (
                    <Card 
                      elevation={isMobile || isTablet ? 2 : 0} 
                      sx={{ 
                        mb: 3, 
                        borderRadius: isMobile || isTablet ? 2 : 0,
                        p: isMobile || isTablet ? 2 : 0
                      }}
                    >
                      <Typography
                        variant={isMobile ? "body2" : "subtitle1"}
                        gutterBottom
                        sx={{ color: 'var(--text-color)', fontWeight: 600, mb: 2 }}
                      >
                        <strong>ID Proof PDF:</strong>
                      </Typography>
                      <Box
                        sx={{
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          maxWidth: '100%'
                        }}
                      >
                        {pdfLoadError.idProof ? (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                              PDF could not be loaded in preview.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Button 
                                variant="outlined" 
                                size="small"
                                onClick={() => window.open(idProofPdfUrl, '_blank')}
                              >
                                Open PDF in New Tab
                              </Button>
                              <Button 
                                variant="text" 
                                size="small"
                                onClick={() => {
                                  setPdfLoadError(prev => ({ ...prev, idProof: false }));
                                  setPdfLoading(prev => ({ ...prev, idProof: true }));
                                }}
                              >
                                Retry Preview
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <>
                            {pdfLoading.idProof && (
                              <Box sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="body2">Loading PDF...</Typography>
                              </Box>
                            )}
                            {/* Try iframe first */}
                            <iframe
                              src={idProofPdfUrl ? `${idProofPdfUrl}#toolbar=0` : ''}
                              width="100%"
                              height={isMobile ? "250px" : isTablet ? "300px" : "400px"}
                              style={{ border: 'none', display: pdfLoading.idProof ? 'none' : 'block' }}
                              title="ID Proof PDF Preview"
                              onError={(e) => {
                                console.error('PDF iframe error:', e);
                                setPdfLoadError(prev => ({ ...prev, idProof: true }));
                                setPdfLoading(prev => ({ ...prev, idProof: false }));
                              }}
                              onLoad={(e) => {
                                console.log('✅ PDF iframe loaded:', idProofPdfUrl);
                                setPdfLoading(prev => ({ ...prev, idProof: false }));
                                // Check if iframe loaded successfully
                                try {
                                  const iframe = e.target;
                                  // If iframe content is accessible, check for errors
                                  if (iframe.contentDocument && iframe.contentDocument.body) {
                                    const bodyText = iframe.contentDocument.body.innerText || '';
                                    if (bodyText.includes('404') || bodyText.includes('Not Found') || bodyText.includes('File not found')) {
                                      console.error('❌ PDF not found in iframe content');
                                      setPdfLoadError(prev => ({ ...prev, idProof: true }));
                                    } else {
                                      console.log('✅ PDF content verified in iframe');
                                    }
                                  }
                                } catch (err) {
                                  // Cross-origin restrictions - can't check content
                                  // This is normal for cross-origin PDFs
                                  console.log('ℹ️ PDF iframe loaded (cross-origin, cannot verify content)');
                                }
                              }}
                            />
                            {/* Fallback object tag for browsers that don't support iframe PDFs */}
                            {!pdfLoading.idProof && (
                              <object
                                data={idProofPdfUrl}
                                type="application/pdf"
                                width="100%"
                                height={isMobile ? "250px" : isTablet ? "300px" : "400px"}
                                style={{ display: 'none' }}
                                onError={() => {
                                  console.log('Object tag also failed, showing error');
                                }}
                              >
                                <Typography variant="body2" align="center" sx={{ p: 2 }}>
                                  PDF preview not available. Please use "Open PDF in New Tab" button.
                                </Typography>
                              </object>
                            )}
                          </>
                        )}
                      </Box>
                      <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        spacing={1} 
                        sx={{ mt: 1, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}
                      >
                        <Button 
                          variant="outlined" 
                          startIcon={<FileDownloadIcon />}
                          onClick={() => window.open(idProofPdfUrl, '_blank')}
                          size={isMobile ? "small" : "medium"}
                          fullWidth={isMobile}
                        >
                          Download
                        </Button>
                        <Button 
                          variant="outlined" 
                          startIcon={<PrintIcon />}
                          onClick={() => handlePrintPdf(idProofPdfUrl)}
                          size={isMobile ? "small" : "medium"}
                          fullWidth={isMobile}
                        >
                          Print
                        </Button>
                      </Stack>
                    </Card>
                  )}
                  {!complaintPdfUrl && !idProofPdfUrl && (
                    <Card elevation={2} sx={{ borderRadius: 2, p: 3 }}>
                      <Typography variant="body2" align="center" color="text.secondary">
                      No PDF files available.
                    </Typography>
                    </Card>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <SmsStatus complaintId={complaint?.complaintId || id} />
          )}

          {activeTab === 2 && (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* Internal Notes */}
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Box sx={{
                    p: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Internal Notes
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      label="Add an internal note"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      inputProps={{ maxLength: 500 }}
                      helperText={`${noteText.length}/500`}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleAddNote}
                        disabled={noteSaving || !noteText.trim()}
                      >
                        Add Note
                      </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    {sortedNotes.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No notes yet.
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {sortedNotes.map((note, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 1.5,
                              backgroundColor: alpha(theme.palette.grey[500], 0.05),
                              borderLeft: '3px solid',
                              borderColor: 'primary.main',
                              borderRadius: 1
                            }}
                          >
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {note.text}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {note.byName || 'Staff'} • {formatDateTime(note.at)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Card>
              </Grid>

              {/* Status & Assignment History */}
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: { xs: 2, sm: 3 } }}>
                  <Box sx={{
                    p: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Status History
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    {(complaint.statusHistory || []).length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No status changes recorded.
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {[...complaint.statusHistory].reverse().map((entry, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {entry.from || 'N/A'} → {entry.to || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {formatDateTime(entry.at)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Card>

                {(complaint.assignmentHistory || []).length > 0 && (
                  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Box sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.03),
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Assignment History
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        {[...complaint.assignmentHistory].reverse().map((entry, index) => (
                          <Box key={index}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {getAssigneeName(entry.assignedTo) || 'Unassigned'}
                            </Typography>
                            {entry.note && (
                              <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                                {entry.note}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {entry.assignedBy ? `By ${getAssigneeName(entry.assignedBy)} • ` : ''}{formatDateTime(entry.at)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Card>
                )}
              </Grid>
            </Grid>
          )}

          {/* Assign / Reassign Dialog (admin only) */}
          <Dialog
            open={assignDialogOpen}
            onClose={() => setAssignDialogOpen(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>Assign / Reassign Complaint</DialogTitle>
            <DialogContent>
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel id="assign-user-label">Assign to</InputLabel>
                <Select
                  labelId="assign-user-label"
                  label="Assign to"
                  value={assignTo}
                  onChange={(e) => setAssignTo(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Unassign</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.username}{user.role ? ` (${user.role})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Note (optional)"
                value={assignNote}
                onChange={(e) => setAssignNote(e.target.value)}
                inputProps={{ maxLength: 500 }}
                helperText={`${assignNote.length}/500`}
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleAssignSave} disabled={assignSaving}>
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Reopen Confirmation Dialog (admin only) */}
          <Dialog
            open={reopenDialogOpen}
            onClose={() => setReopenDialogOpen(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>Reopen Complaint</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                This will set the complaint status back to "Open". Are you sure?
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Reason (optional)"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reopenSendSms}
                    onChange={(e) => setReopenSendSms(e.target.checked)}
                  />
                }
                label="Send status SMS to citizen"
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReopenDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleReopenConfirm}
                disabled={reopenSaving}
              >
                Reopen
              </Button>
            </DialogActions>
          </Dialog>

          {/* Feedback Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Paper>
      </Container>
    </Box>
  );
};

export default ComplaintView;
